import { Currencies } from "@/constants/currency";
import { FieldCurrencyType } from "@/types/app";
import { PaymentTool } from "./accounting";

export interface IPOSConfig {
  id: number;
  name: string; // config name
  description: string; // config description
  users: number[]; // user ids
  ticketGroups: number[]; // ticket groups ids associated to config
  group: number;
  gridsInfo?: { grids: IGrid[] };

  // centralView: object; // centeral bar config
  // ticketEmailsConfig: object;
  // ordersConfig: object;
  paymentConfig?: IPaymentConfig;
  // discounts: object;
  // scannerConfig: object;
  isRefundActivated: boolean;
}

export enum IPOSConfigStatus {
  Active = 1,
  Archived = 2,
}

export interface IGrid {
  ticketGroupId: number;
  name: string;
  type: number;
  buttonColor: string;
  position: number;
  isPricesSeparatedButton: number;
  timeGrid: number;
}

export enum GridTypes {
  Calendar = 1,
  Alphabet,
  Custom,
  value,
  Time,
}

export interface IPaymentConfig {
  cash: {
    used: boolean;
    acceptedCurrencies?: Currencies[]; // found in src/constants/currency.ts
    acceptedBills?: Partial<FieldCurrencyType>; // Check FieldCurrencyType in src/types/app.ts
  };
  cardAuto: {
    used: boolean;
    processor?: string; // ADYEN is the only option now
  };
  cardManual: {
    used: boolean;
    paymentMethods?: string[]; // VISA, TWINT, MASTERCARD.
  };
  invoice: {
    used: boolean;
    acceptedStatuses?: string[]; // Paid | Pending
  };
  toBeInvoiced: {
    used: boolean;
    acceptedStatuses?: string[]; // Paid | Pending
  };
  bankTransfer: {
    used: boolean;
    acceptedStatuses?: string[]; // Paid | Pending
  };
  cheque?: {
    used: boolean;
    acceptedStatuses?: string[]; // Paid | Pending
  };
  chequeVacances?: {
    used: boolean;
    acceptedStatuses?: string[]; // Paid | Pending
  };
  chequeCulture?: {
    used: boolean;
    acceptedStatuses?: string[]; // Paid | Pending
  };
  passCulture?: {
    used: boolean;
    acceptedStatuses?: string[]; // Paid | Pending
  };
  chorus?: {
    used: boolean;
    acceptedStatuses?: string[]; // Paid | Pending
  };
  CE?: {
    used: boolean;
    acceptedStatuses?: string[]; // Paid | Pending
  };
  OCCYG?: {
    used: boolean;
    acceptedStatuses?: string[]; // Paid | Pending
  };
  couponSport?: {
    used: boolean;
    acceptedStatuses?: string[]; // Paid | Pending
  };
  posReka?: {
    used: boolean;
    acceptedStatuses?: string[]; // Paid | Pending
  };
  diverseVoucher?: {
    used: boolean;
    acceptedStatuses?: string[]; // Paid | Pending
  };
  giftCard?: {
    used: boolean;
  };
  invitations?: {
    used: boolean;
  };
  terminal: string; // We keep it here. As we should have 1 terminal per pos config
  CFF?: {
    used: boolean;
    acceptedStatuses?: string[]; // Paid | Pending
  };
  CGN?: {
    used: boolean;
    acceptedStatuses?: string[]; // Paid | Pending
  };
  QOQA?: {
    used: boolean;
    acceptedStatuses?: string[]; // Paid | Pending
  };
  DEINDEAL?: {
    used: boolean;
    acceptedStatuses?: string[]; // Paid | Pending
  };
  REALDEAL?: {
    used: boolean;
    acceptedStatuses?: string[]; // Paid | Pending
  };
  EXTERNAL_INCERT_VOUCHER?: {
    used: boolean;
    acceptedStatuses?: string[]; // Paid | Pending
  };
  Swiss_QR_Code_Bank_Transfer?: {
    used: boolean;
    acceptedStatuses?: string[]; // Paid | Pending
  };
  buyClub?: {
    used: boolean;
    acceptedStatuses?: string[]; // Paid | Pending
  };
  POS_BIMPLI_CADO?: {
    used: boolean;
    acceptedStatuses?: string[]; // Paid | Pending
  };
  POS_KADEOS_INFINI?: {
    used: boolean;
    acceptedStatuses?: string[]; // Paid | Pending
  };
  POS_ANCV_CONNECT?: {
    used: boolean;
    acceptedStatuses?: string[]; // Paid | Pending
  };
  TOUT_UN_CANTON?: {
    used: boolean;
    acceptedStatuses?: string[]; // Paid | Pending
  };
  CHAPLINS?: {
    used: boolean;
    acceptedStatuses?: string[]; // Paid | Pending
  };
  HELLO_CSE?: {
    used: boolean;
    acceptedStatuses?: string[]; // Paid | Pending
  };
  Z_CH_GUTSCHEIN?: {
    used: boolean;
    acceptedStatuses?: string[]; // Paid | Pending
  };
}

export enum PaymentMethodTypes {
  Cash = "cash",
  CardAuto = "cardAuto",
  CardManual = "cardManual",
  Invoice = "invoice",
  ToBeInvoiced = "toBeInvoiced",
  BankTransfer = "bankTransfer",
  // GiftCard = "giftCard",
  Cheque = "cheque",
  ChequeVacances = "chequeVacances",
  chequeCulture = "chequeCulture",
  passCulture = "passCulture",
  chorus = "chorus",
  CE = "CE",
  OCCYG = "OCCYG",
  CouponSport = "couponSport",
  PosReka = "posReka",
  DiverseVoucher = "diverseVoucher",

  CFF = "CFF",
  CGN = "CGN",
  QOQA = "QOQA",
  DEINDEAL = "DEINDEAL",
  REALDEAL = "REALDEAL",

  EXTERNAL_INCERT_VOUCHER = "EXTERNAL_INCERT_VOUCHER",
  Swiss_QR_Code_Bank_Transfer = "Swiss_QR_Code_Bank_Transfer",
  BuyClub = "buyClub",

  POS_BIMPLI_CADO = "POS_BIMPLI_CADO",
  POS_KADEOS_INFINI = "POS_KADEOS_INFINI",
  POS_ANCV_CONNECT = "POS_ANCV_CONNECT",

  TOUT_UN_CANTON = "TOUT_UN_CANTON",
  CHAPLINS = "CHAPLINS",
  HELLO_CSE = "HELLO_CSE",
  Z_CH_GUTSCHEIN = "Z_CH_GUTSCHEIN",
}

/** Invoicing is handled off-till in France — never offered on a French build. */
export const FRENCH_EXCLUDED_PAYMENT_METHODS: PaymentMethodTypes[] = [
  PaymentMethodTypes.Invoice,
  PaymentMethodTypes.ToBeInvoiced,
  PaymentMethodTypes.Swiss_QR_Code_Bank_Transfer,
];

export const PAYMENT_CARDS = [
  "VISA",
  "MASTERCARD",
  "AMEX",
  "TWINT",
  "POSTCARD",
  "PAYPAL",
  "POSTEFINANCE",
];

// object that converts PaymentMethodTypes to PaymentTool
export const PaymentMethodTypeToPaymentTool = {
  [PaymentMethodTypes.Cash]: PaymentTool.Cash,
  [PaymentMethodTypes.CardAuto]: PaymentTool.CardAuto,
  [PaymentMethodTypes.CardManual]: PaymentTool.CardManual,
  [PaymentMethodTypes.Invoice]: PaymentTool.Invoice,
  [PaymentMethodTypes.ToBeInvoiced]: PaymentTool.ToBeInvoiced,
  [PaymentMethodTypes.BankTransfer]: PaymentTool.BankTransfer,
  // [PaymentMethodTypes.GiftCard]: PaymentTool.GiftCard,
  [PaymentMethodTypes.Cheque]: PaymentTool.Cheque,
  [PaymentMethodTypes.ChequeVacances]: PaymentTool.ChequeVacances,
  [PaymentMethodTypes.chequeCulture]: PaymentTool.ChequeCulture,
  [PaymentMethodTypes.passCulture]: PaymentTool.PassCulture,
  [PaymentMethodTypes.chorus]: PaymentTool.Chorus,
  [PaymentMethodTypes.CE]: PaymentTool.CE,
  [PaymentMethodTypes.OCCYG]: PaymentTool.OCCYG,
  [PaymentMethodTypes.CouponSport]: PaymentTool.CouponSport,
  [PaymentMethodTypes.PosReka]: PaymentTool.PosReka,
  [PaymentMethodTypes.DiverseVoucher]: PaymentTool.DiverseVoucher,

  [PaymentMethodTypes.CFF]: PaymentTool.CFF,
  [PaymentMethodTypes.CGN]: PaymentTool.CGN,
  [PaymentMethodTypes.QOQA]: PaymentTool.QOQA,
  [PaymentMethodTypes.DEINDEAL]: PaymentTool.DEINDEAL,
  [PaymentMethodTypes.REALDEAL]: PaymentTool.REALDEAL,

  [PaymentMethodTypes.EXTERNAL_INCERT_VOUCHER]:
    PaymentTool.EXTERNAL_INCERT_VOUCHER,
  [PaymentMethodTypes.Swiss_QR_Code_Bank_Transfer]:
    PaymentTool.Swiss_QR_Code_Bank_Transfer,
  [PaymentMethodTypes.BuyClub]: PaymentTool.BuyClub,

  [PaymentMethodTypes.POS_BIMPLI_CADO]: PaymentTool.POS_BIMPLI_CADO,
  [PaymentMethodTypes.POS_KADEOS_INFINI]: PaymentTool.POS_KADEOS_INFINI,
  [PaymentMethodTypes.POS_ANCV_CONNECT]: PaymentTool.POS_ANCV_CONNECT,

  [PaymentMethodTypes.TOUT_UN_CANTON]: PaymentTool.TOUT_UN_CANTON,
  [PaymentMethodTypes.CHAPLINS]: PaymentTool.CHAPLINS,
  [PaymentMethodTypes.HELLO_CSE]: PaymentTool.HELLO_CSE,
  [PaymentMethodTypes.Z_CH_GUTSCHEIN]: PaymentTool.Z_CH_GUTSCHEIN,
};
